import { useEffect, useMemo, useRef, useState } from "react";
import { Search, X, ArrowRight, Loader2 } from "lucide-react";

/**
 * Ctrl/Cmd + K command palette.
 *
 * Two result tracks:
 *   1. Static page navigation (sections + sub-routes)
 *   2. Cross-journal article search via our FastAPI Crossref proxy
 *      (server-side cached for 10 minutes; filter: IJMRCR + IJSM P-ISSN)
 */
const API = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  { id: "top", label: "Top of page", desc: "Hero", href: "/#top" },
  { id: "about", label: "About the Publisher", desc: "Mission & values", href: "/#about" },
  { id: "journals", label: "Our Journals", desc: "IJMRCR · IJSM", href: "/#journals" },
  { id: "why", label: "Why Publish With Us", desc: "6 commitments", href: "/#why-publish" },
  { id: "standards", label: "Publishing Standards", desc: "Timeline + EQUATOR", href: "/#standards" },
  { id: "authors", label: "For Authors / Reviewers / Editors", desc: "Audiences", href: "/#authors" },
  { id: "call-for-papers", label: "Call for Papers", desc: "Open collections", href: "/#call-for-papers" },
  { id: "evidentica", label: "Evidentica Initiative", desc: "Research visibility platform", href: "/#evidentica" },
  { id: "initiatives", label: "Nutramilo Initiative", desc: "AI clinical-nutrition", href: "/#initiatives" },
  { id: "global", label: "Global Research Community", desc: "World map", href: "/#global" },
  { id: "submit", label: "Submit Manuscript", desc: "Per-journal portal", href: "/#submit" },
  { id: "contact", label: "Publisher Information", desc: "Sofia · Bulgaria", href: "/#contact" },
  { id: "ed-ijmrcr", label: "Editorial Board · IJMRCR", desc: "Medical Reviews & Case Reports", href: "/editorial-board/ijmrcr" },
  { id: "ed-ijsm", label: "Editorial Board · IJSM", desc: "Surgery and Medicine", href: "/editorial-board/ijsm" },
  { id: "accessibility", label: "Accessibility Statement", desc: "WCAG 2.1 AA", href: "/accessibility" },
  { id: "privacy", label: "Privacy Policy", desc: "GDPR-aligned", href: "/privacy" },
  { id: "terms", label: "Terms of Use", desc: "Editorial terms", href: "/terms" },
];

function useDebounced(value, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

async function searchCrossref(q) {
  const url = `${API}/api/crossref-search?q=${encodeURIComponent(q)}&rows=6`;
  const res = await fetch(url, { headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error(`Search ${res.status}`);
  const data = await res.json();
  return data.items || [];
}

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [articles, setArticles] = useState([]);
  const [focusIdx, setFocusIdx] = useState(0);
  const inputRef = useRef(null);
  const debounced = useDebounced(q, 350);

  // Global shortcut
  useEffect(() => {
    const onKey = (e) => {
      const isMac = navigator.platform.toUpperCase().includes("MAC");
      const mod = isMac ? e.metaKey : e.ctrlKey;
      if (mod && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Focus input on open
  useEffect(() => {
    if (open) {
      setQ("");
      setArticles([]);
      setFocusIdx(0);
      setTimeout(() => inputRef.current?.focus(), 30);
      document.documentElement.style.overflow = "hidden";
    } else {
      document.documentElement.style.overflow = "";
    }
  }, [open]);

  // Crossref article search
  useEffect(() => {
    if (!debounced || debounced.trim().length < 3) {
      setArticles([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    searchCrossref(debounced.trim())
      .then((items) => {
        if (!cancelled) setArticles(items);
      })
      .catch(() => {
        if (!cancelled) setArticles([]);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [debounced]);

  const filteredSections = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return SECTIONS;
    return SECTIONS.filter(
      (s) =>
        s.label.toLowerCase().includes(term) ||
        s.desc.toLowerCase().includes(term)
    );
  }, [q]);

  const flatList = useMemo(
    () => [
      ...filteredSections.map((s) => ({ kind: "section", ...s })),
      ...articles.map((a) => ({
        kind: "article",
        id: a.DOI,
        label: Array.isArray(a.title) ? a.title[0] : a.title,
        desc:
          (Array.isArray(a["container-title"])
            ? a["container-title"][0]
            : a["container-title"]) || "",
        href: a.URL || `https://doi.org/${a.DOI}`,
        external: true,
      })),
    ],
    [filteredSections, articles]
  );

  // Keyboard nav
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setFocusIdx((i) => Math.min(i + 1, flatList.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setFocusIdx((i) => Math.max(0, i - 1));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const target = flatList[focusIdx];
        if (target) activate(target);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, focusIdx, flatList]);

  const activate = (item) => {
    setOpen(false);
    if (item.external) {
      window.open(item.href, "_blank", "noopener,noreferrer");
    } else {
      window.location.assign(item.href);
    }
  };

  return (
    <>
      {/* Trigger button (mounted only in header — but we also export self-managed) */}
      {open && (
        <div
          data-testid="command-palette"
          role="dialog"
          aria-modal="true"
          aria-label="Site search"
          className="fixed inset-0 z-[80] flex items-start justify-center pt-[12vh] px-4"
        >
          {/* Overlay */}
          <button
            type="button"
            aria-label="Close search"
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-[var(--sci-navy)]/60 backdrop-blur-sm"
          />

          {/* Panel */}
          <div className="relative w-full max-w-2xl bg-white border border-[var(--sci-border)] shadow-[0_30px_80px_-20px_rgba(11,22,44,0.45)]">
            {/* Search input */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-[var(--sci-border)]">
              <Search size={16} className="text-[var(--sci-teal)] shrink-0" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setFocusIdx(0);
                }}
                data-testid="command-palette-input"
                aria-label="Search sections and articles"
                placeholder="Search sections, journals, or article titles…"
                className="flex-1 bg-transparent outline-none text-base text-[var(--sci-navy)] placeholder:text-[var(--sci-text-muted)]"
                autoComplete="off"
              />
              {loading && (
                <Loader2 size={14} className="text-[var(--sci-teal)] animate-spin" />
              )}
              <kbd className="font-mono-sci text-[10px] tracking-wider text-[var(--sci-text-muted)] border border-[var(--sci-border)] px-1.5 py-0.5">
                ESC
              </kbd>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="text-[var(--sci-text-muted)] hover:text-[var(--sci-navy)]"
              >
                <X size={16} />
              </button>
            </div>

            {/* Results */}
            <div className="max-h-[60vh] overflow-y-auto">
              {filteredSections.length > 0 && (
                <div className="px-3 py-3">
                  <div className="overline text-[var(--sci-text-muted)] px-2 mb-2">
                    Navigate
                  </div>
                  {filteredSections.map((s, i) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => activate({ ...s, kind: "section" })}
                      onMouseEnter={() => setFocusIdx(i)}
                      data-testid={`cmdk-section-${s.id}`}
                      className={`w-full text-left flex items-center justify-between gap-3 px-3 py-2.5 transition-colors ${
                        focusIdx === i
                          ? "bg-[var(--sci-bg)] text-[var(--sci-teal)]"
                          : "text-[var(--sci-navy)] hover:bg-[var(--sci-bg)]"
                      }`}
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">
                          {s.label}
                        </div>
                        <div className="text-xs text-[var(--sci-text-muted)] truncate">
                          {s.desc}
                        </div>
                      </div>
                      <ArrowRight size={13} className="shrink-0 opacity-50" />
                    </button>
                  ))}
                </div>
              )}

              {(loading || articles.length > 0) && (
                <div className="px-3 py-3 border-t border-[var(--sci-border)]">
                  <div className="overline text-[var(--sci-text-muted)] px-2 mb-2">
                    Articles · Crossref
                  </div>
                  {loading && articles.length === 0 && (
                    <div className="px-3 py-4 text-xs text-[var(--sci-text-muted)]">
                      Searching across IJMRCR + IJSM…
                    </div>
                  )}
                  {articles.map((a, i) => {
                    const idx = filteredSections.length + i;
                    return (
                      <button
                        key={a.DOI}
                        type="button"
                        onClick={() =>
                          activate({
                            kind: "article",
                            href: a.URL || `https://doi.org/${a.DOI}`,
                            external: true,
                          })
                        }
                        onMouseEnter={() => setFocusIdx(idx)}
                        data-testid={`cmdk-article-${i}`}
                        className={`w-full text-left flex items-start justify-between gap-3 px-3 py-3 transition-colors ${
                          focusIdx === idx
                            ? "bg-[var(--sci-bg)]"
                            : "hover:bg-[var(--sci-bg)]"
                        }`}
                      >
                        <div className="min-w-0">
                          <div className="font-serif-display text-sm md:text-base text-[var(--sci-navy)] leading-snug">
                            {Array.isArray(a.title) ? a.title[0] : a.title}
                          </div>
                          <div className="text-[11px] font-mono-sci tracking-wider text-[var(--sci-text-muted)] mt-1">
                            {Array.isArray(a["container-title"])
                              ? a["container-title"][0]
                              : a["container-title"]}{" "}
                            · DOI {a.DOI}
                          </div>
                        </div>
                        <ArrowRight
                          size={13}
                          className="shrink-0 opacity-60 mt-1"
                        />
                      </button>
                    );
                  })}
                </div>
              )}

              {!loading &&
                filteredSections.length === 0 &&
                articles.length === 0 && (
                  <div className="px-5 py-10 text-center text-sm text-[var(--sci-text-muted)]">
                    No results. Try a broader term.
                  </div>
                )}
            </div>

            <div className="flex items-center justify-between px-5 py-3 border-t border-[var(--sci-border)] bg-[var(--sci-bg)]">
              <div className="flex items-center gap-3 text-[10px] font-mono-sci tracking-wider text-[var(--sci-text-muted)]">
                <span>↑↓ navigate</span>
                <span>↵ open</span>
                <span>ESC close</span>
              </div>
              <span className="text-[10px] font-mono-sci tracking-wider text-[var(--sci-teal)]">
                CTRL · K
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function useCommandPaletteTrigger() {
  return () => {
    const evt = new KeyboardEvent("keydown", {
      key: "k",
      ctrlKey: true,
      bubbles: true,
    });
    window.dispatchEvent(evt);
  };
}
